<div class="withdraw-form-modal">
  <div class="container">
    <!-- Header -->
    <div class="header-title">
      <mat-icon fontSet="material-icons-outlined" class="header-icon"
        >assignment</mat-icon
      >
      <h6 class="title-large">{{ getModalTitle() }}</h6>
      <button
        mat-icon-button
        class="close-btn"
        (click)="close()"
        [disabled]="isSubmitting"
      >
        <mat-icon fontSet="material-icons-outlined">close</mat-icon>
      </button>
    </div>

    <!-- Loading State -->
    <div *ngIf="isLoading" class="loading-content">
      <mat-spinner diameter="40"></mat-spinner>
      <p>{{ "Loading" | translate }}...</p>
    </div>

    <!-- Error State -->
    <div *ngIf="!isLoading && errorMessage" class="error-content">
      <mat-icon color="warn">error</mat-icon>
      <p>{{ errorMessage }}</p>
      <button class="def-button" (click)="close()">
        {{ "Close" | translate }}
      </button>
    </div>

    <!-- Content with Form -->
    <form
      *ngIf="!isLoading && !errorMessage"
      [formGroup]="withdrawForm"
      class="content"
    >
      <!-- Document Header -->
      <div class="document-header">
        <div class="info-container">
          <p>{{ "To" | translate }}:</p>
          <p>{{ recipientAddress }}</p>
        </div>
      </div>

      <!-- Intro message below the title -->
      <p class="form-intro-message">
        {{ getLocalizedText(config?.modal_intro_message, "WithdrawFormMessage") }}
      </p>

      <p class="form-intro dotted-field">
        {{ getLocalizedText(config?.signature_texts?.legal_preamble, "WithdrawFormIntro") }}
        <span class="contract-date-value field-value">
          {{ withdrawForm.get("contractDate")?.value }}</span
        >
      </p>

      <!-- Client Name (readonly) -->
      <div class="form-field dotted-field">
        <p class="field-label">{{ "WithdrawClientNameLabel" | translate }}:</p>
        <span class="field-value">
          {{ withdrawForm.get("clientName")?.value }}
        </span>
      </div>

      <!-- Client Address (readonly) -->
      <div class="form-field dotted-field">
        <p class="field-label">
          {{ "WithdrawClientAddressLabel" | translate }}:
        </p>
        <span class="field-value">
          {{ withdrawForm.get("clientAddress")?.value }}
        </span>
      </div>

      <!-- Signature (conditional) -->
      <div *ngIf="requireSignature" class="form-field dotted-field signature-container">
        <p class="field-label">
          {{ getLocalizedText(config?.signature_texts?.signature_label, "WithdrawClientSignatureLabel") }}:
        </p>

        <div>
          <signature-pad
            #signature
            [options]="signaturePadOptions"
            (drawEnd)="onSignatureComplete()"
          ></signature-pad>
          <button
            mat-icon-button
            type="button"
            class="clear-signature"
            (click)="clearSignature()"
            [disabled]="isSubmitting"
          >
            <mat-icon fontSet="material-icons-outlined">delete</mat-icon>
          </button>
        </div>
      </div>

      <!-- Current Date (readonly) -->
      <div class="form-field dotted-field">
        <p class="field-label">{{ "WithdrawCurrentDateLabel" | translate }}:</p>
        <span class="field-value">
          {{ withdrawForm.get("currentDate")?.value }}
        </span>
      </div>

      <!-- Config-driven checkboxes -->
      <ng-container *ngIf="config != null && showCheckboxes">
        <h5 class="request-heading">
          {{ getLocalizedText(config?.checkboxes_texts?.heading, "WithdrawRequestHeading") }}
        </h5>
        <p class="confirmation-text">
          {{ getLocalizedText(config?.checkboxes_texts?.confirmation, "WithdrawPleaseConfirm") }}:
        </p>
        <div class="form-field">
          <div class="checkbox-group">
            <mat-checkbox formControlName="cancelAgreement">
              {{ getLocalizedText(config?.checkboxes_texts?.cancel_agreement_label, "WithdrawCancelAgreement") }}
            </mat-checkbox>
            <mat-checkbox formControlName="withdrawFromCase">
              {{ getLocalizedText(config?.checkboxes_texts?.withdraw_from_case_label, "WithdrawWithdrawFromCase") }}
            </mat-checkbox>
          </div>
        </div>
      </ng-container>

      <!-- Legacy termination form (checkboxes + radio) -->
      <ng-container *ngIf="showLegacyQuestionnaire">
        <h5 class="request-heading">
          {{ "WithdrawRequestHeading" | translate }}
        </h5>

        <p class="confirmation-text">
          {{ "WithdrawPleaseConfirm" | translate }}:
        </p>

        <div class="form-field">
          <div class="checkbox-group">
            <mat-checkbox formControlName="cancelAgreement">
              {{ "WithdrawCancelAgreement" | translate }}
            </mat-checkbox>
            <mat-checkbox formControlName="withdrawFromCase">
              {{
                "WithdrawWithdrawFromCase"
                  | translate : { caseName: data.caseName }
              }}
            </mat-checkbox>
          </div>
          <div
            *ngIf="
              (withdrawForm.get('cancelAgreement')?.touched ||
                withdrawForm.get('withdrawFromCase')?.touched) &&
              withdrawForm.hasError('atLeastOneRequired')
            "
            class="error-message"
          >
            {{ "WithdrawAtLeastOneRequired" | translate }}
          </div>
        </div>

        <div class="form-field">
          <label class="field-label" style="margin-bottom: 8px">
            {{ "WithdrawReasonLabel" | translate }}:
          </label>
          <mat-radio-group formControlName="reason">
            <mat-radio-button value="no_longer_interested">
              {{
                "WithdrawReasonNoLongerInterested"
                  | translate : { caseName: data.caseName }
              }}
            </mat-radio-button>
            <mat-radio-button value="no_longer_want_representation">
              {{ "WithdrawReasonNoLongerWantRepresentation" | translate }}
            </mat-radio-button>
            <mat-radio-button value="claimant_passed_away">
              {{ "WithdrawReasonClaimantPassedAway" | translate }}
            </mat-radio-button>
            <mat-radio-button value="other">
              {{ "WithdrawReasonOther" | translate }}
            </mat-radio-button>
          </mat-radio-group>
          <div
            *ngIf="
              withdrawForm.get('reason')?.touched &&
              withdrawForm.get('reason')?.hasError('required')
            "
            class="error-message"
          >
            {{ "WithdrawReasonRequired" | translate }}
          </div>
        </div>

        <div class="form-field" *ngIf="isOtherReasonSelected()">
          <textarea
            formControlName="otherReasonText"
            maxlength="250"
            rows="4"
            class="other-reason-textarea"
            [placeholder]="'WithdrawOtherReasonPlaceholder' | translate"
          ></textarea>
          <div class="char-counter">
            {{ withdrawForm.get('otherReasonText')?.value?.length || 0 }}/250
          </div>
          <div
            *ngIf="
              withdrawForm.get('otherReasonText')?.touched &&
              withdrawForm.get('otherReasonText')?.hasError('required')
            "
            class="error-message"
          >
            {{ "WithdrawOtherReasonRequired" | translate }}
          </div>
          <div
            *ngIf="
              withdrawForm.get('otherReasonText')?.touched &&
              withdrawForm.get('otherReasonText')?.hasError('minlength')
            "
            class="error-message"
          >
            {{ "WithdrawOtherReasonMinLength" | translate }}
          </div>
        </div>
      </ng-container>

      <!-- Config-driven dynamic questionnaire -->
      <ng-container *ngIf="configuredQuestions.length > 0" [formGroup]="dynamicAnswers">
        <div *ngFor="let question of configuredQuestions" class="form-field">
          <!-- Single choice -->
          <ng-container *ngIf="question.type === 'single_choice'">
            <label class="field-label" style="margin-bottom: 8px">
              {{ getLocalizedLabel(question) }}
              <span *ngIf="question.required !== false" class="required-mark">*</span>
            </label>
            <mat-radio-group [formControlName]="question.id">
              <mat-radio-button *ngFor="let opt of question.options" [value]="opt.value">
                {{ getOptionLabel(opt) }}
              </mat-radio-button>
            </mat-radio-group>
            <div
              *ngIf="dynamicAnswers.get(question.id)?.touched && dynamicAnswers.get(question.id)?.hasError('required')"
              class="error-message"
            >
              {{ "WithdrawReasonRequired" | translate }}
            </div>
          </ng-container>

          <!-- Multi choice -->
          <ng-container *ngIf="question.type === 'multi_choice'">
            <label class="field-label" style="margin-bottom: 8px">
              {{ getLocalizedLabel(question) }}
              <span *ngIf="question.required !== false" class="required-mark">*</span>
            </label>
            <div class="checkbox-group">
              <mat-checkbox *ngFor="let opt of question.options"
                [checked]="(dynamicAnswers.get(question.id)?.value || []).includes(opt.value)"
                (change)="toggleMultiChoice(question.id, opt.value, $event.checked)"
              >
                {{ getOptionLabel(opt) }}
              </mat-checkbox>
            </div>
          </ng-container>

          <!-- Free text -->
          <ng-container *ngIf="question.type === 'free_text'">
            <label class="field-label" style="margin-bottom: 8px">
              {{ getLocalizedLabel(question) }}
              <span *ngIf="question.required !== false" class="required-mark">*</span>
            </label>
            <textarea
              [formControlName]="question.id"
              [maxlength]="question.max_length || 500"
              rows="4"
              class="other-reason-textarea"
            ></textarea>
            <div class="char-counter">
              {{ dynamicAnswers.get(question.id)?.value?.length || 0 }}/{{ question.max_length || 500 }}
            </div>
            <div
              *ngIf="dynamicAnswers.get(question.id)?.touched && dynamicAnswers.get(question.id)?.hasError('required')"
              class="error-message"
            >
              {{ "WithdrawReasonRequired" | translate }}
            </div>
            <div
              *ngIf="dynamicAnswers.get(question.id)?.touched && dynamicAnswers.get(question.id)?.hasError('minlength')"
              class="error-message"
            >
              {{ "WithdrawOtherReasonMinLength" | translate }}
            </div>
          </ng-container>
        </div>
      </ng-container>
    </form>

    <!-- Footer Actions -->
    <div *ngIf="!isLoading && !errorMessage" class="actions">
      <button
        class="def-button alt"
        (click)="close()"
        [disabled]="isSubmitting"
      >
        {{ "Cancel" | translate }}
      </button>

      <button
        class="def-button"
        (click)="submit()"
        [disabled]="isSubmitting || !hasSignature() || withdrawForm.invalid || dynamicAnswers.invalid"
      >
        {{
          isSubmitting
            ? ("Submitting" | translate)
            : getSubmitButtonLabel()
        }}
      </button>
    </div>
  </div>
</div>
